import { useState } from 'react';

import { Modal, Notice } from '@/components/Modal';
import { type MyRelocation } from '@/pages/mypage/useWelfare';

/**
 * 숙소 신청서 (DOC-68 §5.10 · 사용자 지적 2026-08-27: 「희망은 어디서 쓰는건데?
 * 숙소 신청 누르면 모달이 뜨는거야?」).
 *
 * ## 왜 생겼나 — 채울 길 없는 칸이 셋 있었다
 * 인사팀 승인 창은 「희망: 원룸 선호」·「신청 인원 2명」·「입주 2026-09-01」 을 보여 주게 만들어
 * 두었는데, 신청은 **버튼 한 번에 빈 몸으로** 나갔다. 그래서 그 자리는 언제나 비어 있었고,
 * 담당자는 화면에 있는 칸을 보고 「왜 아무도 안 적지」 라고 물을 수밖에 없었다.
 *
 * ## 그래도 결재가 아니다
 * 창이 하나 뜨는 것과 결재를 타는 것은 다르다. [요청 보내기]를 누르면 **그대로 인사팀 목록에
 * 선다** — 상신도 결재선도 없다(DOC-68 §5.0).
 *
 * ## 빈 채로 보내도 된다
 * 모르는 것을 억지로 적게 하지 않는다. 날짜는 비우면 **발령 기간**이 들어가고(서버가 안다),
 * 희망 사항은 없으면 없는 것이다. 필수 칸이 하나도 없는 이유다.
 */
export function LodgingRequestModal({
  eligibility,
  pending,
  error,
  onSubmit,
  onClose,
}: {
  eligibility: NonNullable<MyRelocation['eligibility']>;
  pending: boolean;
  error: string | null;
  onSubmit: (body: {
    checkInDate?: string | null;
    checkOutDate?: string | null;
    occupants?: number;
    roomPreference?: string | null;
    note?: string | null;
  }) => void;
  onClose: () => void;
}) {
  /*
    발령 기간을 <b>미리 채워 둔다</b>. 이미 서버가 아는 값이고, 사람이 다시 적으면 거기서 틀린다 —
    발령일을 잘못 옮긴 신청은 인사팀이 방을 배정한 뒤에야 드러난다. 고칠 수는 있다(실제 입주는
    발령일과 며칠 다른 일이 흔하다).
  */
  const [checkIn, setCheckIn] = useState(eligibility.assignmentStartDate ?? '');
  const [checkOut, setCheckOut] = useState(eligibility.assignmentEndDate ?? '');
  const [occupants, setOccupants] = useState('1');
  const [preference, setPreference] = useState('');
  const [note, setNote] = useState('');

  return (
    <Modal title="숙소 신청" onClose={onClose} maxWidth="520px" closeDisabled={pending}>
      {/*
        판정 근거를 먼저 보여 준다 — 신청하는 사람도 「내가 왜 대상인지」 를 알고 눌러야 한다.
        인사팀 창과 같은 순서다(근거 → 고를 것).
      */}
      <div className="flex items-center gap-2 flex-wrap" style={{ marginBottom: '4px' }}>
        <span className={eligibility.tier === 'MANDATORY' ? 'badge badge-green' : 'badge badge-yellow'}>
          {eligibility.tierLabel}
        </span>
        <span style={{ fontSize: '13px' }}>{eligibility.siteName}</span>
      </div>
      <p style={mutedText}>
        {eligibility.residenceLabel ?? '거주지 미등록'} → {eligibility.siteName}
        {eligibility.distanceKm != null && ` · ${eligibility.distanceKm}km`}
        {eligibility.travelMinutes != null && ` · ${eligibility.travelMinutes}분`}
      </p>

      <div className="flex items-end gap-3 flex-wrap" style={{ marginTop: '12px' }}>
        <label style={{ fontSize: '13px' }}>
          <span style={labelText}>입주 예정일</span>
          <input
            className="input-field"
            type="date"
            aria-label="입주 예정일"
            style={inputStyle}
            value={checkIn}
            onChange={(e) => setCheckIn(e.target.value)}
          />
        </label>
        <label style={{ fontSize: '13px' }}>
          <span style={labelText}>퇴거 예정일</span>
          <input
            className="input-field"
            type="date"
            aria-label="퇴거 예정일"
            style={inputStyle}
            value={checkOut}
            onChange={(e) => setCheckOut(e.target.value)}
          />
        </label>
        <label style={{ fontSize: '13px' }}>
          {/* 「함께 지낼 인원」 이다 — 2인실에 혼자 들어갈지 부부가 들어갈지가 배정을 가른다. */}
          <span style={labelText}>함께 지낼 인원</span>
          <input
            className="input-field"
            type="number"
            min={1}
            aria-label="함께 지낼 인원"
            style={{ ...inputStyle, width: '72px' }}
            value={occupants}
            onChange={(e) => setOccupants(e.target.value)}
          />
        </label>
      </div>
      <p style={{ ...mutedText, marginTop: '2px' }}>
        날짜를 비우면 <b>발령 기간</b>이 들어갑니다. 무기한 배치면 퇴거일은 비워 두세요.
      </p>

      <label style={{ fontSize: '13px', display: 'block', marginTop: '12px' }}>
        <span style={labelText}>희망 사항</span>
        {/*
          이 값이 인사팀 승인 창에 「희망: …」 으로 그대로 뜬다. 배정을 <b>강제하지는 않는다</b> —
          방 사정은 인사팀만 알고, 적어 둔 대로 못 되는 날이 있다. 그래도 적어 두면 고려된다.
        */}
        <input
          className="input-field"
          aria-label="희망 사항"
          placeholder="예: 원룸 선호 · 1층 희망 · 비흡연"
          style={{ ...inputStyle, width: '100%' }}
          value={preference}
          onChange={(e) => setPreference(e.target.value)}
        />
      </label>
      <p style={mutedText}>인사팀이 방을 정할 때 참고합니다 — 그대로 배정된다는 뜻은 아닙니다.</p>

      <label style={{ fontSize: '13px', display: 'block', marginTop: '10px' }}>
        <span style={labelText}>비고</span>
        <input
          className="input-field"
          aria-label="비고"
          placeholder="그 밖에 알려야 할 사정"
          style={{ ...inputStyle, width: '100%' }}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </label>

      {error && <Notice tone="error">{error}</Notice>}

      <div className="flex items-center gap-2" style={{ marginTop: '14px', justifyContent: 'flex-end' }}>
        <span style={{ ...mutedText, marginRight: 'auto' }}>
          결재를 거치지 않고 <b>인사팀에 바로</b> 갑니다.
        </span>
        <button style={{ fontSize: '13px' }} onClick={onClose} disabled={pending}>취소</button>
        <button
          className="btn-primary"
          style={{ fontSize: '13px', padding: '6px 14px' }}
          disabled={pending}
          onClick={() => onSubmit({
            // 빈 문자열은 <b>키째로 빼서</b> 보낸다. 서버가 「안 보냈다」 와 「빈 값으로 정했다」 를
            // 가려야 발령 기간으로 채울 수 있다.
            checkInDate: checkIn || null,
            checkOutDate: checkOut || null,
            occupants: Math.max(1, Number(occupants) || 1),
            roomPreference: preference.trim() || null,
            note: note.trim() || null,
          })}
        >
          {pending ? '보내는 중…' : '요청 보내기'}
        </button>
      </div>
    </Modal>
  );
}

const mutedText = { fontSize: '12px', color: 'var(--text-muted)' } as const;
const labelText = { display: 'block', color: 'var(--text-secondary)', marginBottom: '2px' } as const;
const inputStyle = { fontSize: '13px', padding: '4px 6px' } as const;
